import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useToast } from "@/contexts";
import { createRematch, getCompletionSummary } from "@/features/competitions/api";
import { api } from "@/lib/api";
import type { BadgeDefinition, CompetitionSummary } from "@/types";

const CONGRATS_MESSAGES = [
  "You crushed it!",
  "Legendary performance!",
  "Steps well done!",
  "The miles flew by!",
  "What a journey!",
  "Victory is yours!",
];

function getRandomCongrats() {
  return CONGRATS_MESSAGES[Math.floor(Math.random() * CONGRATS_MESSAGES.length)];
}

interface CompetitionBadgeWithUser {
  badge: BadgeDefinition;
  userDisplayName: string;
}

export function CompletedCompetitionView({
  competition,
  currentUserId,
}: {
  competition: CompetitionSummary;
  currentUserId: string;
}) {
  const [badgePodium, setBadgePodium] = useState<CompetitionBadgeWithUser[]>([]);
  const [loadingBadges, setLoadingBadges] = useState(true);
  const [summary, setSummary] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [startingRematch, setStartingRematch] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get<{ badges: CompetitionBadgeWithUser[] }>(
          `/api/competitions/${competition.id}/badges/podium`,
        );
        if (data?.badges) setBadgePodium(data.badges);
      } catch {
      } finally {
        setLoadingBadges(false);
      }
    })();
  }, [competition.id]);

  useEffect(() => {
    const loadSummary = async () => {
      const { data } = await getCompletionSummary(competition.id);
      if (data) setSummary(data.message);
      setLoadingSummary(false);
    };
    loadSummary();
  }, [competition.id]);

  const toast = useToast();

  const handleRematch = async () => {
    if (startingRematch) return;
    setStartingRematch(true);
    const { data, error } = await createRematch(competition.id);
    setStartingRematch(false);
    if (error) {
      toast.showAlert("Rematch failed", error.message, "error");
      return;
    }
    if (data) navigate({ to: `/competition/${data}` });
  };

  const sortedLeaderboard = [...(competition.leaderboard ?? [])].sort((a, b) => b.steps - a.steps);
  const winner = sortedLeaderboard[0];
  const isWinner = winner?.userId === currentUserId;

  return (
    <div className="flex flex-col gap-6 items-center py-8 px-4">
      <div className="text-6xl">🏆</div>

      <div className="text-center">
        <h2 className="text-text text-2xl font-extrabold tracking-tight">
          {competition.name} is complete!
        </h2>
        {winner && (
          <p className="text-text-muted text-base mt-2">
            Winner: <span className="text-aqua font-extrabold">{winner.name}</span> with{" "}
            {winner.steps.toLocaleString()} steps
            {isWinner && " — that's you! 🎉"}
          </p>
        )}
      </div>

      <div className="bg-card rounded-[20px] border-2 border-text shadow-[2px_2px_0px_0px_#1c1b2e] p-6 w-full max-w-md">
        <h3 className="text-text text-lg font-extrabold mb-4 text-center">{getRandomCongrats()}</h3>

        <div className="flex flex-col gap-2">
          {sortedLeaderboard.slice(0, 5).map((entry, idx) => {
            const medals = ["🥇", "🥈", "🥉"];
            const rankDisplay = medals[idx] || `#${idx + 1}`;
            return (
              <div
                key={entry.userId}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  entry.userId === currentUserId
                    ? "bg-[rgba(255,45,126,0.06)] border border-[rgba(255,45,126,0.2)]"
                    : ""
                }`}
              >
                <span className="text-xl w-8 text-center">{rankDisplay}</span>
                <span className="flex-1 text-text font-bold text-sm">{entry.name}</span>
                <span className="text-text-muted text-sm font-bold">
                  {entry.steps.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {loadingSummary && (
        <div className="animate-pulse bg-card rounded-[20px] border-2 border-text shadow-[2px_2px_0px_0px_#1c1b2e] p-6 w-full max-w-md">
          <div className="h-16 bg-[rgba(28,27,46,0.06)] rounded-lg" />
        </div>
      )}
      {!loadingSummary && summary && (
        <div className="bg-card rounded-[20px] border-2 border-text shadow-[2px_2px_0px_0px_#1c1b2e] p-6 w-full max-w-md">
          <h3 className="text-text-muted text-xs font-bold uppercase tracking-wider mb-2">
            💬 Recap
          </h3>
          <p className="text-text text-sm leading-relaxed">{summary}</p>
        </div>
      )}

      <button
        type="button"
        onClick={async () => {
          const top5 = sortedLeaderboard.slice(0, 5);
          const medals = ["🥇", "🥈", "🥉"];
          const lb = top5
            .map(
              (e, i) =>
                `  ${medals[i] || `#${i + 1}`}: ${e.name} — ${e.steps.toLocaleString()} steps`,
            )
            .join("\n");
          const recapLine = summary ? `${summary}\n\n` : "";
          const text = `🏆 FootSlapping: ${competition.name} — Final Results!\n\n${recapLine}Final Leaderboard:\n${lb}\n\nmade with 🐾 and ❤️ by FootSlapping\nhttps://footslapping.com`;
          const url = window.location.href;
          if (navigator.share) {
            try {
              await navigator.share({
                title: `FootSlapping: ${competition.name} — Final Results`,
                text,
                url,
              });
            } catch {}
          } else {
            try {
              await navigator.clipboard.writeText(`${text}\n\n${url}`);
              toast.showAlert("Copied!", "Competition summary copied to clipboard", "success");
            } catch {
              toast.showAlert("Competition summary", text, "info");
            }
          }
        }}
        className="w-full max-w-md mt-2 bg-white border-2 border-text rounded-2xl py-3 px-4 shadow-[2px_2px_0px_0px_#1c1b2e]"
      >
        <span className="text-text text-[0.875rem] font-black inline-flex items-center gap-2">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
          Share results
        </span>
      </button>

      {!competition.rematchCompetitionId && (
        <button
          type="button"
          onClick={handleRematch}
          disabled={startingRematch}
          className="w-full max-w-md bg-text border-2 border-text rounded-2xl py-3 px-4 shadow-[2px_2px_0px_0px_#1c1b2e] disabled:opacity-60"
        >
          <span className="text-white text-[0.875rem] font-black inline-flex items-center gap-2 justify-center">
            {startingRematch ? "Starting rematch…" : "🔁 Start a rematch"}
          </span>
        </button>
      )}

      {badgePodium.length > 0 && (
        <div className="bg-card rounded-[20px] border-2 border-text shadow-[2px_2px_0px_0px_#1c1b2e] p-6 w-full max-w-md">
          <h3 className="text-text text-lg font-extrabold mb-4 text-center">Badge Champions</h3>
          <div className="grid grid-cols-3 gap-3">
            {badgePodium.map((item) => (
              <div key={item.badge.badgeId} className="flex flex-col items-center gap-1 p-2">
                <span className="text-3xl">{item.badge.iconEmoji}</span>
                <span className="text-[0.625rem] font-bold text-text text-center leading-tight">
                  {item.badge.name}
                </span>
                <span className="text-[0.625rem] text-text-dim text-center">
                  {item.userDisplayName}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loadingBadges && (
        <div className="animate-pulse bg-card rounded-[20px] border-2 border-text shadow-[2px_2px_0px_0px_#1c1b2e] p-6 w-full max-w-md">
          <div className="flex justify-center gap-4">
            {Array.from({ length: 3 }, (_, i) => i).map((n) => (
              <div key={n} className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-[rgba(28,27,46,0.08)]" />
                <div className="w-16 h-3 bg-[rgba(28,27,46,0.08)] rounded" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
